/**
 * TAM 上报
 */
import Aegis from 'aegis-web-sdk';
import { isEmpty, isNil } from 'lodash';
import Cookies from 'universal-cookie';

import { getLoginCookies } from '@/login/login';
import { envName, isLocal, isProd, isUgc, isWoa } from '@/utils/getApiDomain';

import { aegisV2 } from './tamv2';

// 子域名
const cookies = new Cookies(null, { path: '/', domain: '.dreamai.fun' });

export const getUserId = () => cookies.get('vuserid') || cookies.get('vuser');;

const vuid = getUserId();

const name = envName();

const getReportId = () => {
  if (isWoa) {
    if (isProd) {
      return 'r5kJXIlZ3Owz2DZnmg';
    }
    return 'RrGvdtPwadkRZmGera';
  }
  if (isUgc) {
    if (isProd) {
      return 'QVxe0cJjZY0aqrD80v';
    }
    return 'qVZwqcLjPZx3r798Ow';
  }
};

export const aegis = new Aegis({
  id: getReportId(), // 上报 id
  uin: vuid,
  reportApiSpeed: true, // 接口测速
  reportAssetSpeed: true, // 静态资源测速
  spa: true, // spa 应用页面跳转的时候开启 pv 计算
  hostUrl: 'https://rumt-zh.com',
  env: name,
  api: {
    apiDetail: true,
    reportRequest: true,
  },
});

export declare enum LogType {
  INFO_ALL = '-1',
  API_RESPONSE = '1',
  INFO = '2', //  aegis.info、aegis.infoAll 上报的日志
  ERROR = '4',
  PROMISE_ERROR = '8',
  AJAX_ERROR = '16',
  SCRIPT_ERROR = '32',
  IMAGE_ERROR = '64',
  CSS_ERROR = '128',
  CONSOLE_ERROR = '256',
  MEDIA_ERROR = '512',
  RET_ERROR = '1024',
  REPORT = '2048',
  PV = '4096',
  EVENT = '8192',
  PAGE_NOT_FOUND_ERROR = '16384',
  WEBSOCKET_ERROR = '32768',
  BRIDGE_ERROR = '65536',
  LAZY_LOAD_ERROR = '131072',
}

export enum CustomReportType {
  CUSTOM_INFO = '自定义上报信息',
  LOGIN_REFRESH = '登录续期',
  LOGIN_REFRESH_SUCCESS = '登录续期成功',
  LOGIN_REFRESH_ERROR = '登录刷新错误',
  LOGIN_STATUS_INVALID = '登录状态失效',
  RESPONSE_ERROR_MESSAGE = '接口错误信息',
  RESPONSE_ERROR_CODE = '接口错误CODE',
  TRPC_ERROR_CODE = 'TRPC接口错误CODE',
  CLEAR_COOKIE = '清除Cookie',
  IMAGE_FILTER = '图片滤镜上报',
  CHANGE_ENV = '切换环境',
  MORE_REQUEST = '并发请求',
  SCENE_INFO = '幕信息上报',
  MISSION_INFO = '剧情信息上报',
  MISSION_EXCL_INFO = '剧情解析上报',
  PAY_INFO = '付费信息上报',
  NOT_SAFE_BROWSER = '浏览器版本过低不支持部分属性',
  CLEAR_COOKIE_FROM = '清除Cookie来源',
  UPLOAD_FILE = '上传文件',
}

export const sesitiveApis: string[] = [
  '/finance/create_contract_draft',
  '/finance/contract_detail_list',
];

export function shouldIgnoreLog(level: LogType, msg: string): boolean {
  if (msg.indexOf('Can not get editor instance') > -1) {
    return true;
  }
  // 过滤发布器报错。
  if (level === Aegis.logType.ERROR && msg.indexOf('cwebview::runJS') > -1) {
    return true;
  }
  // 针对用户敏感信息不上报
  if (sesitiveApis.some(api => msg.includes(api))) {
    return true;
  }
  // 过滤 chrome-extension 报错
  if (level === Aegis.logType.ERROR && msg.indexOf('chrome-extension') > -1) {
    return true;
  }

  if (/^script error\.?($|\s?@)/i.test(msg)) {
    return true;
  }
  return false;
}

const reportInfo = (params: { [key: string]: string }) => {
  aegis.info({ ...params }); // rum上报
  if (aegisV2) {
    aegisV2?.info({ ...params }); // TAMV2上报
  }
};

const getReportMessage = (params: { [key: string]: any }) => JSON.stringify(params);

export const customReport = (
  type: CustomReportType,
  params: { [key: string]: any },
) => {
  const str = getReportMessage(params);
  reportInfo({ msg: `【${type}】:${str}` });
};

export const reportResponse = (
  response: Response,
  url: string,
  data: any,
  httpErrorMsg: string,
) => {
  const errorMsg = response.headers.get('Trpc-Error-Msg') || '';
  const errorCode = response.headers.get('Trpc-Func-Ret') || '';
  const params = getLoginCookies();
  const reportMsg = {
    url,
    errorCode,
    errorMsg,
    code: data.code,
    data,
    cookies: params,
    httpErrorMsg,
  };
  if (url?.includes('RefreshToken')) {
    if (errorMsg || errorCode || data?.ret !== 0 || isEmpty(data?.data)) {
      customReport(CustomReportType.LOGIN_REFRESH_ERROR, reportMsg);
    }
  }
  if (errorMsg || errorCode || httpErrorMsg) {
    // 区分接口和code，因为剧情数据量会巨大，所以拆分上报内容，防止被截断
    customReport(CustomReportType.RESPONSE_ERROR_CODE, {
      errorMsg,
      errorCode,
      httpErrorMsg,
    });
    customReport(CustomReportType.RESPONSE_ERROR_MESSAGE, {
      ...reportMsg,
      httpErrorMsg: data?.httpErrorMsg,
    });
  }
};

export const reportInit = () => {
  if (isLocal) return;
  aegis.init({
    uin: vuid,
    beforeReport(log: { level: LogType; msg: string }) {
      const { msg, level } = log;
      if (shouldIgnoreLog(level, msg)) {
        return false;
      }
      return true;
    },
    api: {
      apiDetail: true,
      reportRequest: true,
      retCodeHandler(ret: string) {
        let isError = true;
        let code;
        try {
          const retObj = JSON.parse(ret || '{}');
          code = retObj.code || retObj.ret;
          if (isNil(code) || code === 0 || code === '0') {
            isError = false;
          }
        } catch (e) {
          isError = true;
          code = `parse error:${ret}`;
        }
        return { isErr: isError, code };
      },
    },
  });
};
